import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, Tabs, Tab } from '@mui/material';
import { CalendarMonth, EventNote, InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SimpleCalendarView from './SimpleCalendarView';
import MyReservations from './MyReservations';
import StiklainisRulesGrid from './stiklainis/StiklainisRulesGrid';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" id={`stiklainis-tabpanel-${index}`} aria-labelledby={`stiklainis-tab-${index}`} sx={{ p: { xs: 2, sm: 3 } }}>
      {children}
    </Box>
  );
}

const Stiklainis: React.FC = () => {
  const { t } = useTranslation();

  const getInitialTab = (): number => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('stiklainisTab');
      if (fromQuery !== null) {
        const parsed = parseInt(fromQuery, 10);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 2) return parsed;
      }
      const fromStorage = window.localStorage.getItem('stiklainisTab');
      if (fromStorage !== null) {
        const parsed = parseInt(fromStorage, 10);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 2) return parsed;
      }
    } catch { /* ignore */ }
    return 0;
  };

  const [tabValue, setTabValue] = useState(getInitialTab);
  const [selectedRoom, setSelectedRoom] = useState<string>('');

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('stiklainisTab', String(tabValue));
      window.history.replaceState({}, '', url.toString());
      window.localStorage.setItem('stiklainisTab', String(tabValue));
    } catch { /* ignore */ }
  }, [tabValue]);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          aria-label="stiklainis tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 500 },
          }}
        >
          <Tab icon={<CalendarMonth />} iconPosition="start" label={t('stiklainis.calendarView')} />
          <Tab icon={<EventNote />} iconPosition="start" label={t('stiklainis.myReservations')} />
          <Tab icon={<InfoOutlined />} iconPosition="start" label={t('stiklainis.roomInformation')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <SimpleCalendarView
            selectedRoom={selectedRoom}
            onRoomChange={setSelectedRoom}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MyReservations />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <StiklainisRulesGrid />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Stiklainis;
